<template>

    <div class="wrapper">

        <div v-if="pagedAndFilteredData.length > 0" class="row align-items-center my-2 d-print-none">
            <div class="col-12 mx-auto d-sm-block d-md-none form-group">
                <select2-component class="form-control-lg"
                                   v-model="pagingData.sortInformation"
                                   :allowclear="false"
                                   :addblankentry="false"
                                   :options="sortByDropDownOptions"
                                   width="100%"></select2-component>
            </div>
        </div>

        <div class="row align-items-center my-2 d-print-none">

            <div class="col-12 col mx-my-auto">

                <grid-pager-component :id="topPagerName"
                                      v-bind:class="pagerCssClass"
                                      :current-page="pagingData.currentPageIndex"
                                      :records-per-page="pagingData.howManyRecordsPerPage"
                                      :full-data-set="data"
                                      :sort-column="pagingData.sortColumn"
                                      :sort-direction="pagingData.sortDirection"
                                      :pager-column-css="pagerColumnCssClass"
                                      @change="onPageChangeFromPager"
                                      @pagerIsVisible="pagerIsVisible"
                                      @total-number-of-pages-change="totalNumberOfPagesChange">
                    <template v-slot:pager-column-before="props">
                        <slot name="grid-pager-column-before-top"></slot>
                    </template>
                    <template v-slot:pager-column-after="props">
                        <slot name="grid-pager-column-after-top"></slot>
                    </template>
                </grid-pager-component>
            </div>

        </div>

        <slot name="grid-pre-body"></slot>

        <div v-if="pagedAndFilteredData.length > 0" class="row no-gutters w-100 table-header">

            <div class="col-md" v-bind:class="columnConfig.cssClass" @click.prevent="onHeaderSortClick(columnConfig)" v-for="columnConfig in columns" :key="columnConfig.columnId" data-ui="grid-header" :data-ui-id="columnConfig.columnId">

                <div v-if="columnConfig.disableSort" class="h6 font-weight-bold d-none d-md-block d-lg-block mskgrid-custom-columnstyle">
                    {{ columnConfig.columnText }}
                </div>
                <a v-else href="#">

                    <div class="h6 font-weight-bold text-uppercase d-none d-md-block d-lg-block mskgrid-custom-columnstyle">
                        {{ columnConfig.columnText }}
                        <i v-show="sortColumn == columnConfig.columnId && sortDirection =='desc'" class="fa fa-chevron-down text-portal-primary-blue text-primary c-pointer" :aria-label="'Data is sorted by ' + columnConfig.columnText + ' descending'" aria-hidden="true"></i>
                        <i v-show="sortColumn == columnConfig.columnId && sortDirection =='asc'" class="fa fa-chevron-up text-portal-primary-blue text-primary c-pointer" :aria-label="'Data is sorted by ' + columnConfig.columnText + ' ascending'" aria-hidden="true"></i>
                    </div>
                </a>

            </div>

        </div>

        <hr class="table-header-body-separator" />

        <div class="table-body-container rounded">
            <div v-if="pagedAndFilteredData.length == 0" class="row no-gutters w-100 align-items-center">
                <div class="col-12 col-md-12 text-center">
                    <span class="text-capitalize font-weight-bold">{{ noRecords }}</span>
                </div>
            </div>
            <div v-else v-for="record in pagedAndFilteredData">
                <slot name="grid-body" v-bind:gridRecord="record"></slot>
            </div>
        </div>

        <div class="row align-items-center my-4 d-print-none">
            <div class="col mx-my-auto">

                <grid-pager-component :id="bottomPagerName"
                                      v-bind:class="pagerCssClass"
                                      :current-page="pagingData.currentPageIndex"
                                      :records-per-page="pagingData.howManyRecordsPerPage"
                                      :full-data-set="data"
                                      :sort-column="pagingData.sortColumn"
                                      :sort-direction="pagingData.sortDirection"
                                      :pager-column-css="pagerColumnCssClass"
                                      @change="onPageChangeFromPager">
                    <template v-slot:pager-column-before="props">
                        <slot name="grid-pager-column-before-bottom"></slot>
                    </template>
                    <template v-slot:pager-column-after="props">
                        <slot name="grid-pager-column-after-bottom"></slot>
                    </template>
                </grid-pager-component>
            </div>

        </div>

    </div>
</template>

<script>

    import jlinq from '@SharedServices/linq.javascript.service.js';
    import './datagrid.scss';
    import gridPagerComponent from '@PortalVueComponents/Pager/generic.pager.vue';
    import select2Component from '@SharedVueComponents/select2/select2.vue';
    import { ResolveStaticContent } from '@SharedServices/localization.service';

    export default {
        name: "grid",
        props: {
            howManyRecordsPerPage: { type: Number },
            initialSortColumn: { type: String },
            initialSortDirection: { type: String }, //asc vs desc
            data: { type: Array },
            columns: { type: Array }, //[ {columnId: , columnText: , sortAscTitle: 'sort a to z', sortDescTitle: 'sort z to a', 'cssClass: 'text-right', disableSort: true [optional defaults to false] }]
            pagerCssClass: { type: Object },
            pagerColumnCssClass: { type: Object },
            noRecords: { type: String, default: ResolveStaticContent('GridNoRecord') },//can flip to this when all pages that use the grid are translated
            pageText: { type: String, default: ResolveStaticContent('PagerPageText') }, //can flip to this when all pages that use the grid are translated
            pageOfText: { type: String, default: ResolveStaticContent('PagerPageOfText') } //can flip to this when all pages that use the grid are translated
        },
        components: {
            'grid-pager-component': gridPagerComponent,
            'select2-component': select2Component
        },
        data() {
            return {
                //component id so we can generate a unique pager
                id: null,

                pagingData: {
                    howManyRecordsPerPage: this.howManyRecordsPerPage,
                    currentPageIndex: 1,
                    sortInformation: this.initialSortColumn + '_' + this.initialSortDirection
                }
            }
        },
        mounted: function () {
            this.id = this._uid
        },
        computed: {

            sortByDropDownOptions: function () {
                if (!this.columns) {
                    return [];
                }

                let data = [];

                for (var i = 0; i < this.columns.length; i++) {
                    let thisColumn = this.columns[i];

                    if (!thisColumn.disableSort) {
                        data.push({ id: thisColumn.columnId + '_asc', text: thisColumn.sortAscTitle });
                        data.push({ id: thisColumn.columnId + '_desc', text: thisColumn.sortDescTitle });
                    }
                }

                return data;
            },

            pagedAndFilteredData: function () {

                let query = this.data.AsQueryable();

                if (this.sortDirection == 'asc') {
                    query = query.OrderBy(x => x[this.sortColumn]);
                }
                else {
                    query = query.OrderByDescending(x => x[this.sortColumn]);
                }

                //this returns essentially the final data set that was filtered and paged and should be outputted on the page
                return query.ToArray().Paginate(this.pagingData.currentPageIndex, this.pagingData.howManyRecordsPerPage).ToArray();
            },

            sortColumn: function () {
                return this.pagingData.sortInformation.split('_')[0];
            },

            sortDirection: function () {
                return this.pagingData.sortInformation.split('_')[1];
            },

            topPagerName: function () {
                return this.id + 'grid-top-pager';
            },

            bottomPagerName: function () {
                return this.id + 'grid-bottom-pager';
            }
        },
        watch: {

            'pagingData.sortInformation': {
                handler: function (val, oldVal) {
                    //reset the page back to 1
                    this.pagingData.currentPageIndex = 1;
                },
                deep: true
            },
            data: function (newVal, oldVal) {
                //reset the page back to 1
                this.pagingData.currentPageIndex = 1;
            }
        },
        methods: {

            onHeaderSortClick: function (column) {

                if (column.disableSort) {
                    return;
                }

                let direction = 'asc';

                if (column.columnId == this.sortColumn) {
                    direction = this.sortDirection == 'desc' ? 'asc' : 'desc'
                }

                this.pagingData.sortInformation = column.columnId + '_' + direction;

            },

            windowScrollToPager: function () {

                let pager = $("#" + this.topPagerName);

                if (pager && pager.length > 0) {
                    $("html, body").animate({ scrollTop: pager.offset().top }, "medium");
                }
            },

            onPageChangeFromPager: function (pageIndex, pagerElementId) {
                //the appointmentPagerComponent has a change in page. We want to set this. (not using 2 way binding. Passing in data and event it back out to this component)
                this.pagingData.currentPageIndex = pageIndex;

                //go to the top of the future appointments only if we click on the bottom pager
                if (pagerElementId == this.bottomPagerName) {
                    this.windowScrollToPager();
                }
            },

            pagerIsVisible: function (isPagerVisible) {
                this.$emit('pager-is-visible', isPagerVisible);
            },

            totalNumberOfPagesChange: function (totalNumberOfPagesInGrid) {

                //if a user has 3 pages...then they delete a record then they only have 2 pages in the grid (but they are on page 3). So it becomes an invalid page.
                //if we hit that scenario just put them on the last page
                if (totalNumberOfPagesInGrid > 0 && totalNumberOfPagesInGrid < this.pagingData.currentPageIndex) {
                    //his this scenario...send them to the last page
                    this.pagingData.currentPageIndex = totalNumberOfPagesInGrid;
                }
            }
        }
    }
</script>